/**
 * ToolsSubMenu component - Level 2 submenu for a selected mode
 * Layout: horizontal container with a 24px title rail (rotated text) and a 44px-wide column of tool buttons
 */

import { createIconButton, type IconButtonAPI } from "@UI/IconButton/index.js";
import { createIcon } from "@UI/Icon/index.js";
import "./ToolsSubMenu.scss";

export type ToolMode = "sketch" | "edit" | "ds" | "cms";

export interface SubMenuItem {
  readonly id: string;
  readonly icon: string;
}

export interface ToolsSubMenuConfig {
  readonly mode: ToolMode;
  readonly title?: string; // defaults to mode
  readonly items: readonly SubMenuItem[];
  readonly selectedItemId?: string | undefined;
  readonly onSelect?: (itemId: string, meta?: { wasSelected?: boolean }) => void;
  readonly onItemDoubleClick?: (
    itemId: string,
    anchor?: HTMLElement | null
  ) => void;
  readonly onItemLongPress?: (
    itemId: string,
    anchor?: HTMLElement | null
  ) => void;
  readonly onBack?: () => void; // clicking title area navigates back
}

export interface ToolsSubMenuAPI {
  readonly getElement: () => HTMLElement;
  readonly setItems: (items: readonly SubMenuItem[]) => void;
  readonly setSelectedItem: (id: string | null) => void;
  readonly getMode: () => ToolMode;
  readonly destroy: () => void;
}

export const createToolsSubMenu = (
  config: ToolsSubMenuConfig
): ToolsSubMenuAPI => {
  const {
    mode,
    title = config.mode,
    items,
    selectedItemId,
    onSelect,
    onItemDoubleClick,
    onItemLongPress,
    onBack,
  } = config;

  let isDestroyed = false;
  let currentItems: SubMenuItem[] = [...items];
  const buttonAPIs: { id: string; api: IconButtonAPI }[] = [];
  let currentSelectedId: string | null = selectedItemId ?? null;
  const isTouchDevice =
    typeof window !== "undefined"
      ? "ontouchstart" in window || navigator.maxTouchPoints > 0
      : false;

  // Root container
  const container = document.createElement("div");
  container.className = "tools-sub-menu";

  // Title rail (24px wide)
  const titleRail = document.createElement("div");
  titleRail.className = "tools-sub-menu__title-group";
  titleRail.setAttribute("aria-label", `${title} back`);
  titleRail.addEventListener("click", () => {
    if (isDestroyed) return;
    onBack?.();
  });

  const titleText = document.createElement("div");
  titleText.textContent = title;
  titleText.className = "tools-sub-menu__title";

  const titleIcon = createIcon({
    name: "modes_" + mode,
    size: 32,
  });
  titleRail.appendChild(titleIcon.getElement());

  titleRail.appendChild(titleText);
  container.appendChild(titleRail);

  // Column container for items
  const column = document.createElement("div");
  column.className = "tools-sub-menu__column";
  if (isTouchDevice) {
    column.classList.add("touch-device");
  }
  container.appendChild(column);

  const renderItems = (): void => {
    // Clear previous
    column.innerHTML = "";
    buttonAPIs.splice(0, buttonAPIs.length);

    currentItems.forEach((item) => {
      let skipClick = false;
      let pendingSelect = false;
      let pressWasSelected = false;
      const commitSelection = (
        nextId: string,
        meta?: { wasSelected?: boolean }
      ) => {
        currentSelectedId = nextId;
        buttonAPIs.forEach(({ id, api }) => api.setSelected(id === nextId));
        onSelect?.(nextId, meta);
      };

      const api = createIconButton({
        icon: item.icon,
        size: 44,
        iconSize: 28,
        behavior: "button",
        selected: item.id === currentSelectedId,
        className: "tools-sub-menu__item",
        onPointerDown: () => {
          pressWasSelected = item.id === currentSelectedId;
          if (!pressWasSelected) {
            skipClick = true;
            pendingSelect = true;
            currentSelectedId = item.id;
            buttonAPIs.forEach(({ id, api }) =>
              api.setSelected(id === item.id)
            );
          }
        },
        onPointerUp: () => {
          if (!pendingSelect) return;
          pendingSelect = false;
          commitSelection(item.id, { wasSelected: false });
        },
        onClick: () => {
          if (skipClick) {
            skipClick = false;
            return;
          }
          commitSelection(item.id, { wasSelected: true });
        },
        onLongPress: () => {
          if (!pressWasSelected) return;
          skipClick = true;
          onItemLongPress?.(item.id, api.getElement());
        },
        longPressPointerTypes: ["touch", "pen", "mouse"],
      });
      buttonAPIs.push({ id: item.id, api });
      column.appendChild(api.getElement());
    });
  };

  renderItems();

  const destroy = (): void => {
    if (isDestroyed) return;
    isDestroyed = true;
    buttonAPIs.forEach(({ api }) => api.destroy());
    if (container.parentNode) container.parentNode.removeChild(container);
  };

  return {
    getElement: () => container,
    setItems: (itemsList: readonly SubMenuItem[]) => {
      if (isDestroyed) return;
      currentItems = [...itemsList];
      renderItems();
    },
    setSelectedItem: (id: string | null) => {
      if (isDestroyed) return;
      const nextId = typeof id === "string" ? id : null;
      if (currentSelectedId === nextId) return;
      currentSelectedId = nextId;
      // Ensure button APIs exist; if not yet, re-render once with selected
      if (buttonAPIs.length === 0) renderItems();
      buttonAPIs.forEach(({ id: bid, api }) => api.setSelected(bid === nextId));
    },
    getMode: () => mode,
    destroy,
  };
};

export const createToolsSubMenuInContainer = (
  container: HTMLElement,
  config: ToolsSubMenuConfig
): ToolsSubMenuAPI => {
  const sub = createToolsSubMenu(config);
  container.appendChild(sub.getElement());
  return sub;
};
